<template>
  <v-container>
    <v-row class="main-container">
      <v-col>
        <v-card class="mx-auto" max-width="1000">
          <v-card-text>
            <v-avatar size="140" class="ml-3">
              <img :src="profile.profileImageUrl" />
            </v-avatar>
            <div>
              <h2 class="ml-3">
                {{ formatName(profile.name) }}
              </h2>
              <h2 class="ml-3">
                {{ profile.other && profile.other.pni ? profile.other.pni + ' ' : '' }}
              </h2>
            </div>
          </v-card-text>
          <v-card-text class="ml-3">
            <v-checkbox
              v-model="free15"
              label="Schedule a Free 15 minute phone or video chat before your appointment.

Please note that not every Professional will offer a free 15 minute phone call."
            ></v-checkbox>
          </v-card-text>
          <v-card-text class="ml-3"> Select appointment date </v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-card-text>
                <v-date-picker
                  elevation="3"
                  full-width
                  v-model="dateString"
                  :allowed-dates="allowedDates"
                  class="mt-4"
                  style="margin-top: 0px !important"
                  :events="events"
                  :picker-date.sync="pickerDate"
                ></v-date-picker>

                <v-alert v-if="error" type="error">
                  {{ error }}
                </v-alert>

                <!-- Updated Multi-Step Dialog -->
                <v-dialog v-model="dialog" max-width="800" persistent>
                  <v-card>
                    <!-- Step 1: Appointment Details -->
                    <div v-if="currentStep === 1">
                      <v-card-title class="headline"> New Appointment: </v-card-title>
                      <v-card-text> Member: You </v-card-text>
                      <v-card-text> Professional: {{ formatName(profile.name) }} </v-card-text>
                      <v-card-text>
                        {{ createEvent ? formatDate(createEvent.start) : '' }}
                      </v-card-text>
                      <v-card-text>
                        {{ createEvent ? formatDate(createEvent.end) : '' }}
                      </v-card-text>
                      <!-- Price Breakdown -->
                      <v-card-text v-if="!free15 && appointmentRate > 0">
                        <v-divider class="mb-3"></v-divider>
                        <div class="d-flex justify-space-between align-center">
                          <span class="text-h6">Appointment Rate</span>
                          <span class="text-h6 font-weight-bold">${{ appointmentRate.toFixed(2) }}</span>
                        </div>
                        <v-divider class="mt-3"></v-divider>
                      </v-card-text>

                      <v-card-text>
                        <v-autocomplete
                          label="Location"
                          v-model="selectedPreferredLocation"
                          :items="preferredLocationsFiltered"
                          chips
                          item-text="text"
                          item-value="id"
                          return-object
                        >
                          <template v-slot:append>
                            <v-icon color="#428751">mdi-arrow-down-drop-circle</v-icon>
                          </template>
                        </v-autocomplete>
                        <v-text-field
                          v-model="address"
                          label="Address"
                          v-if="selectedPreferredLocation && selectedPreferredLocation.id != 3"
                          :disabled="selectedPreferredLocation && selectedPreferredLocation.id != 4"
                        ></v-text-field>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn class="mb-2 main-btn" outlined color="gray" text @click="cancelAppointment()">
                          Cancel
                        </v-btn>
                        <v-btn
                          class="mb-2 main-btn success-green"
                          text
                          @click="proceedToPayment()"
                          :disabled="!selectedPreferredLocation"
                        >
                          {{ free15 || !appointmentRate || appointmentRate === 0 ? 'Schedule' : 'Proceed to Payment' }}
                        </v-btn>
                        <v-spacer></v-spacer>
                      </v-card-actions>
                    </div>

                    <!-- Step 2: Payment -->
                    <div v-else-if="currentStep === 2">
                      <v-card-title class="headline">
                        <v-btn icon @click="currentStep = 1" class="mr-2">
                          <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        Payment
                      </v-card-title>

                      <v-card-text>
                        <!-- Price Summary -->
                        <v-card outlined class="mb-4">
                          <v-card-title>
                            <v-icon left color="primary">mdi-cash</v-icon>
                            Price Summary
                          </v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>Appointment Rate:</span>
                              <span class="text-h6 font-weight-bold">${{ appointmentRate.toFixed(2) }}</span>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <div class="d-flex justify-space-between align-center">
                              <span class="text-h6">Total:</span>
                              <span class="text-h6 font-weight-bold primary--text"
                                >${{ appointmentRate.toFixed(2) }}</span
                              >
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Loading state -->
                        <div v-if="loadingPaymentMethods" class="text-center py-8">
                          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                          <p class="mt-2">Loading payment methods...</p>
                        </div>

                        <!-- Existing Payment Methods -->
                        <div v-else-if="existingPaymentMethods.length > 0 && !showNewPaymentForm">
                          <v-card outlined class="mb-4">
                            <v-card-title>
                              <v-icon left color="primary">mdi-credit-card-multiple</v-icon>
                              Select Payment Method
                            </v-card-title>
                            <v-card-text>
                              <v-radio-group v-model="selectedPaymentMethodId">
                                <v-radio v-for="pm in existingPaymentMethods" :key="pm.id" :value="pm.id" class="mb-3">
                                  <template v-slot:label>
                                    <div class="d-flex align-center">
                                      <v-icon left>{{ getCardIcon(pm.card.brand) }}</v-icon>
                                      <span>
                                        <strong>{{ pm.card.brand.toUpperCase() }}</strong>
                                        •••• {{ pm.card.last4 }}
                                        <span class="grey--text ml-2"
                                          >Exp: {{ pm.card.exp_month }}/{{ pm.card.exp_year }}</span
                                        >
                                      </span>
                                    </div>
                                  </template>
                                </v-radio>
                              </v-radio-group>

                              <v-btn text color="primary" @click="showNewPaymentForm = true" class="mt-2">
                                <v-icon left>mdi-credit-card-plus</v-icon>
                                Add New Payment Method
                              </v-btn>
                            </v-card-text>
                          </v-card>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn outlined color="grey" @click="currentStep = 1"> Back </v-btn>
                            <v-btn
                              color="primary"
                              @click="proceedWithSelectedPayment"
                              :disabled="!selectedPaymentMethodId"
                            >
                              Continue
                            </v-btn>
                          </v-card-actions>
                        </div>

                        <!-- New Payment Method Form -->
                        <div v-else>
                          <v-btn
                            v-if="existingPaymentMethods.length > 0"
                            text
                            color="primary"
                            @click="showNewPaymentForm = false"
                            class="mb-4"
                          >
                            <v-icon left>mdi-arrow-left</v-icon>
                            Back to Saved Cards
                          </v-btn>

                          <StripePaymentForm
                            v-if="setupIntentClientSecret"
                            :client-secret="setupIntentClientSecret"
                            :current-user-email="selfProfile?.email"
                            :metadata="paymentMetadata"
                            cta-button-text="Save & Continue"
                            @payment-method-saved="handlePaymentMethodSaved"
                            @setup-error="handleSetupError"
                            @cancel="currentStep = 1"
                          />
                        </div>
                      </v-card-text>
                    </div>

                    <!-- Step 3: Confirmation -->
                    <div v-else-if="currentStep === 3">
                      <v-card-title class="headline text-center">
                        <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
                        Appointment Scheduled!
                      </v-card-title>

                      <v-card-text class="text-center">
                        <p class="text-h6 mb-4">Your appointment has been successfully booked.</p>

                        <v-card outlined class="mx-auto" max-width="400">
                          <v-card-text>
                            <div class="text-left">
                              <strong>Professional:</strong> {{ formatName(profile.name) }}<br />
                              <strong>Date & Time:</strong> {{ createEvent ? formatDate(createEvent.start) : '' }}<br />
                              <strong>Location:</strong> {{ selectedPreferredLocation?.text }}<br />
                              <strong v-if="!free15 && appointmentRate > 0">Amount Charged:</strong>
                              <span v-if="!free15 && appointmentRate > 0"> ${{ appointmentRate.toFixed(2) }}</span
                              ><br />
                              <strong v-if="paymentMethodSaved">Payment Method:</strong>
                              <span v-if="paymentMethodSaved"> Saved Successfully</span>
                            </div>
                          </v-card-text>
                        </v-card>

                        <p class="mt-4 text-body-2">
                          A confirmation email has been sent to you with all the details. You will also receive a
                          reminder 24 hours before your appointment.
                        </p>
                      </v-card-text>

                      <v-card-actions class="justify-center">
                        <v-btn class="mb-2 main-btn success-green" text @click="closeModal()"> Done </v-btn>
                      </v-card-actions>
                    </div>

                    <!-- Error display -->
                    <v-alert v-if="errorDialog" type="error" class="mx-4 mb-4">
                      {{ errorDialog }}
                    </v-alert>
                  </v-card>
                </v-dialog>
              </v-card-text>
            </v-col>

            <v-col cols="12" sm="6" v-if="available && available.length > 0">
              <v-card-text>
                <v-card>
                  <v-card-text>
                    <v-list style="height: 344px; overflow: scroll">
                      <v-list-item v-for="item in available" :key="item.starts" @click="createAppointment(item)">
                        <v-list-item-content>
                          <v-list-item-title
                            v-text="formatDate2(item.start) + '-' + formatDate2(item.end)"
                          ></v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
/* eslint-disable no-debugger */

import API from '../../api';
import moment from 'moment';
import { formatName } from '@/util/stringUtils';
import StripePaymentForm from '@/components/stripe/StripePaymentForm.vue';

const preferredHome = 1;
const preferredClient = 2;
const preferredOnline = 3;
const preferredAgreedUpon = 4;

export default {
  name: 'create-appointment',
  components: {
    StripePaymentForm
  },
  props: {
    username: {
      type: [String]
    }
  },
  data: () => ({
    // Original data
    dateString: null,
    events: [],
    schedule: [],
    pickerDate: null,
    error: '',
    errorDialog: '',
    dialog: false,
    createEvent: null,
    start: null,
    end: null,
    available: [],
    profile: {},
    selfProfile: {},
    hours: [],
    free15: false,
    selectedPreferredLocation: null,
    address: null,
    preferredLocations: [
      { text: "Professional's Location", id: preferredHome, location: 'Home' },
      { text: "Member's Location", id: preferredClient, location: 'Client' },
      { text: 'Web Conferencing', id: preferredOnline, location: 'Online' },
      {
        text: 'Agreed Upon Location',
        id: preferredAgreedUpon,
        location: 'AgreedUpon'
      }
    ],
    preferredLocationsFiltered: [],

    // Payment-related data
    currentStep: 1,
    setupIntentClientSecret: null,
    paymentMethodSaved: false,
    stripeCustomerId: null,
    stripePaymentMethodId: null,
    cardHolderName: null,

    // Existing payment methods
    existingPaymentMethods: [],
    selectedPaymentMethodId: null,
    loadingPaymentMethods: false,
    showNewPaymentForm: false,

    // Pricing
    appointmentRate: 0
  }),

  computed: {
    paymentMetadata() {
      return {
        professionalUsername: this.username,
        appointmentStart: this.createEvent?.start,
        appointmentEnd: this.createEvent?.end,
        location: this.selectedPreferredLocation?.location,
        address: this.address,
        free15: this.free15
      };
    }
  },

  watch: {
    pickerDate(val) {
      this.updateStartEnd(new Date(val));
    },
    dateString(val) {
      this.loadAvailableTimes(val);
    },
    free15() {
      this.updateStartEnd(new Date(this.pickerDate));
    },
    selectedPreferredLocation(preferredLocation) {
      if (!preferredLocation) return;

      if (preferredLocation.id === preferredHome) {
        this.address = this.profile && this.profile.other ? this.profile.other.address : null;
      }
      if (preferredLocation.id === preferredClient) {
        this.address = this.selfProfile && this.selfProfile.personalInfo ? this.selfProfile.personalInfo.address : null;
      }
      if (preferredLocation.id === preferredOnline) {
        this.address = null;
      }
      if (preferredLocation.id === preferredAgreedUpon) {
        this.address = '';
      }
    }
  },

  async created() {
    let res = await API.get('profile?username=' + encodeURI(this.username));
    if (!res) {
      return;
    }
    if (res.errors && res.errors.length > 0) {
      this.error = res.errors[0].msg;
    } else {
      this.error = null;
      this.profile = res.data.user;
      console.log(`profile`, this.profile);

      // Set appointment rate - check multiple possible field names
      this.appointmentRate = this.profile?.other?.appointmentRate || 0;
      console.log(`appointmentRate`, this.appointmentRate);
      // this.profile.sessionPrice ||
      // this.profile.price ||
      // this.profile.hourlyRate ||
      // this.profile.rate ||
      // (this.profile.other && this.profile.other.sessionPrice) ||
      // (this.profile.other && this.profile.other.price) ||
      // 100; // Default fallback

      let preferredLocationsFiltered = [];
      if (this.profile.preferredHome) {
        preferredLocationsFiltered.push(this.preferredLocations[0]);
      }
      if (this.profile.preferredClient) {
        preferredLocationsFiltered.push(this.preferredLocations[1]);
      }
      if (this.profile.preferredOnline) {
        preferredLocationsFiltered.push(this.preferredLocations[2]);
      }
      if (this.profile.preferredAgreedUpon || preferredLocationsFiltered.length === 0) {
        preferredLocationsFiltered.push(this.preferredLocations[3]);
      }
      this.preferredLocationsFiltered = preferredLocationsFiltered;
      if (preferredLocationsFiltered.length === 1) {
        this.selectedPreferredLocation = preferredLocationsFiltered[0];
      }
      this.images = res.data.images;
      res = await API.get('profile');
      if (!res) {
        this.error = 'no response';
      }
      if (res.errors && res.errors.length > 0) {
        this.error = res.errors[0].msg;
      } else {
        this.selfProfile = res.data.user;

        // Set stripeCustomerId if it exists in profile
        if (this.selfProfile.stripeCustomerId) {
          this.stripeCustomerId = this.selfProfile.stripeCustomerId;
        }
      }
    }
  },

  mounted() {
    document.title = 'Create Appointment';
  },

  methods: {
    formatName(name) {
      return formatName(name);
    },

    getCardIcon(brand) {
      const icons = {
        visa: 'mdi-credit-card',
        mastercard: 'mdi-credit-card',
        amex: 'mdi-credit-card',
        discover: 'mdi-credit-card',
        diners: 'mdi-credit-card',
        jcb: 'mdi-credit-card',
        unionpay: 'mdi-credit-card'
      };
      return icons[brand?.toLowerCase()] || 'mdi-credit-card';
    },

    async loadExistingPaymentMethods() {
      this.loadingPaymentMethods = true;
      this.errorDialog = null;

      try {
        const res = await API.get('stripe/payment-methods');

        if (res.errors && res.errors.length > 0) {
          console.error('Error loading payment methods:', res.errors[0].msg);
          // If no payment methods exist, that's not necessarily an error
          this.existingPaymentMethods = [];
        } else if (res.data && res.data.paymentMethods) {
          this.existingPaymentMethods = res.data.paymentMethods;
          this.stripeCustomerId = res.data.customerId;

          // Auto-select the default payment method if available
          const defaultPM = this.existingPaymentMethods.find(pm => pm.isDefault);
          if (defaultPM) {
            this.selectedPaymentMethodId = defaultPM.id;
          } else if (this.existingPaymentMethods.length > 0) {
            // Select the first one if no default
            this.selectedPaymentMethodId = this.existingPaymentMethods[0].id;
          }
        }
      } catch (error) {
        console.error('Failed to load payment methods:', error);
        this.existingPaymentMethods = [];
      } finally {
        this.loadingPaymentMethods = false;
      }
    },

    async updateStartEnd(date) {
      this.getEvents(moment(date).subtract(1, 'M').toDate(), moment(date).add(1, 'M').toDate());
    },

    functionEvents(date) {
      const [, , day] = date.split('-');
      if ([12, 17, 28].includes(parseInt(day, 10))) return true;
      if ([1, 19, 22].includes(parseInt(day, 10))) return ['red', '#00f'];
      return false;
    },

    async getEvents(start, end) {
      const events = [];
      let res = await API.post('schedule/load', {
        username: this.username,
        start: start,
        end: end,
        sessionType: this.free15 ? 'Free15' : 'Standard'
      });
      if (res.errors && res.errors.length > 0) {
        this.error = res.errors[0].msg;
        return;
      }
      this.hours = res.data.workHours;

      for (let i = 0; i < res.data.schedule.length; i++) {
        let scheduleEntry = res.data.schedule[i];
        const first = new Date(scheduleEntry.start);
        events.push(first.toISOString().substr(0, 10));
      }
      this.schedule = res.data.schedule;
      this.events = events;
    },

    formatDate(date) {
      return moment(date).format('LLL');
    },

    formatDate2(date) {
      return moment(date).format('LT');
    },

    formatDateForPicker(date) {
      return moment(date).format('YYYY-mm-dd');
    },

    workHourForDate: function (date) {
      //find date
      for (let index = 0; index < this.hours.length; index++) {
        const workhour = this.hours[index];
        if (workhour.date === date && workhour.hidden) {
          return workhour;
        }
      }
      for (let index = 0; index < this.hours.length; index++) {
        const workhour = this.hours[index];
        if (workhour.date === date) {
          return workhour;
        }
      }
      //if no date - find day of week
      let momentValue = moment(date);
      for (let index = 0; index < this.hours.length; index++) {
        const workhour = this.hours[index];
        if (workhour.dayType === 'Mon' && momentValue.day() === 0) {
          return workhour;
        }
        if (workhour.dayType === 'Tue' && momentValue.day() === 1) {
          return workhour;
        }
        if (workhour.dayType === 'Wed' && momentValue.day() === 2) {
          return workhour;
        }
        if (workhour.dayType === 'Thu' && momentValue.day() === 3) {
          return workhour;
        }
        if (workhour.dayType === 'Fri' && momentValue.day() === 4) {
          return workhour;
        }
        if (workhour.dayType === 'Sat' && momentValue.day() === 5) {
          return workhour;
        }
        if (workhour.dayType === 'Sun' && momentValue.day() === 6) {
          return workhour;
        }
      }
      return null;
    },

    allowedDates: function (date) {
      if (new Date(date).getTime() < new Date().getTime()) {
        return false;
      }
      let workHour = this.workHourForDate(date);
      if (!workHour) {
        return false;
      }
      return workHour.work;
    },

    loadAvailableTimes: async function (date) {
      if (date) {
        let res = await API.post('schedule/available', {
          username: this.username,
          date: date,
          sessionType: this.free15 ? 'Free15' : 'Standard'
        });
        if (res.errors && res.errors.length > 0) {
          this.error = res.errors[0].msg;
          return;
        }
        this.checkAndSetAvailable(res.data);
      } else {
        this.available = [];
      }
    },

    checkAndSetAvailable: async function (dates) {
      this.available = dates;
    },

    createAppointment: async function (event) {
      this.createEvent = event;
      this.error = null;
      this.dialog = true;
      this.currentStep = 1;
    },

    async proceedToPayment() {
      if (!this.selectedPreferredLocation) {
        this.errorDialog = 'Please select location';
        return;
      }

      // Skip payment for free consultations OR when appointmentRate is null/0
      if (this.free15 || !this.appointmentRate || this.appointmentRate === 0) {
        await this.scheduleAppointment();
        if (!this.dialog) {
          // If scheduleAppointment closed the dialog due to error, don't proceed
          return;
        }
        this.currentStep = 3; // Show confirmation
      } else {
        // Load existing payment methods first
        await this.loadExistingPaymentMethods();

        // Create setup intent for new payment method (always needed as fallback)
        this.errorDialog = null;
        try {
          let res = await API.post('stripe/create-setup-intent');
          if (res.errors && res.errors.length > 0) {
            this.errorDialog = res.errors[0].msg;
            return;
          }

          this.setupIntentClientSecret = res.data.client_secret;

          // If customer has existing payment methods, show them
          // Otherwise, show new payment form
          if (this.existingPaymentMethods.length === 0) {
            this.showNewPaymentForm = true;
          }

          this.currentStep = 2;
        } catch (error) {
          console.error('Setup intent creation error:', error);
          this.errorDialog = 'Failed to initialize payment. Please try again.';
        }
      }
    },

    async proceedWithSelectedPayment() {
      if (!this.selectedPaymentMethodId) {
        this.errorDialog = 'Please select a payment method';
        return;
      }

      // Set the payment method info
      this.stripePaymentMethodId = this.selectedPaymentMethodId;

      // Find the selected payment method details
      const selectedPM = this.existingPaymentMethods.find(pm => pm.id === this.selectedPaymentMethodId);

      if (selectedPM) {
        this.cardHolderName = selectedPM.billing_details?.name || '';
      }

      // Proceed to schedule the appointment
      try {
        await this.scheduleAppointment();
        this.currentStep = 3; // Show confirmation
      } catch (error) {
        this.errorDialog = 'Failed to book appointment. Please try again.';
        console.error('Appointment creation error:', error);
      }
    },

    async handlePaymentMethodSaved(data) {
      console.log('Payment method saved:', data);
      this.paymentMethodSaved = true;
      this.stripeCustomerId = data.stripeCustomerId;
      this.stripePaymentMethodId = data.stripePaymentMethodId;
      this.cardHolderName = data.cardHolderName;

      try {
        // Now schedule the appointment
        await this.scheduleAppointment();
        this.currentStep = 3; // Show confirmation
      } catch (error) {
        this.errorDialog = 'Payment method saved but appointment booking failed. Please try again.';
        console.error('Appointment creation error:', error);
      }
    },

    handleSetupError(error) {
      console.error('Setup error:', error);
      this.errorDialog = 'Failed to save payment method. Please try again.';
    },

    async scheduleAppointment() {
      if (!this.selectedPreferredLocation) {
        this.errorDialog = 'Please select location';
        return;
      }
      this.errorDialog = null;

      const requestBody = {
        professionalUsername: this.username,
        start: this.createEvent.start,
        end: this.createEvent.end,
        location: this.selectedPreferredLocation.location,
        address: this.address
      };

      // Add payment information if not a free consultation and rate > 0
      if (!this.free15 && this.appointmentRate && this.appointmentRate > 0) {
        requestBody.stripeCustomerId = this.stripeCustomerId;
        requestBody.stripePaymentMethodId = this.stripePaymentMethodId;
        requestBody.cardHolderName = this.cardHolderName;
        requestBody.amount = this.appointmentRate;
      }

      let res = await API.post('appointment/create', requestBody);

      if (res.errors && res.errors.length > 0) {
        this.error = res.errors[0].msg;
        return;
      }

      // Don't automatically set step 3 here - let the caller decide

      // Reset for next appointment
      this.createEvent = null;
      await this.updateStartEnd(new Date(this.pickerDate));
      this.dateString = null;
    },

    closeModal() {
      this.dialog = false;
      this.currentStep = 1;
      this.createEvent = null;
      this.setupIntentClientSecret = null;
      this.paymentMethodSaved = false;
      this.stripeCustomerId = null;
      this.stripePaymentMethodId = null;
      this.cardHolderName = null;
      this.errorDialog = null;
      this.existingPaymentMethods = [];
      this.selectedPaymentMethodId = null;
      this.showNewPaymentForm = false;
    },

    cancelAppointment() {
      this.dialog = false;
      this.currentStep = 1;
      this.createEvent = null;
      this.setupIntentClientSecret = null;
      this.paymentMethodSaved = false;
      this.stripeCustomerId = null;
      this.stripePaymentMethodId = null;
      this.cardHolderName = null;
      this.errorDialog = null;
      this.existingPaymentMethods = [];
      this.selectedPaymentMethodId = null;
      this.showNewPaymentForm = false;
    }
  }
};
</script>

<style scoped>
/* Add any additional styles here */
</style>
